import { zodResolver } from '@hookform/resolvers/zod';
import { SupportCreateCompanyRequest, type SupportCreateCompanyResponse } from '@siteday/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import type { z } from 'zod';

import { errorMessage } from '@/api/client';
import * as supportApi from '@/api/support';
import { qk } from '@/api/support';
import { PageHeader } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Card, CardBody, CardHeader } from '@/components/ui/table';

type FormInput = z.input<typeof SupportCreateCompanyRequest>;
type FormOutput = z.output<typeof SupportCreateCompanyRequest>;

/** `tenant:create` from the browser (RUNBOOK-tenant-provisioning.md step 1). The password shows once. */
export function NewCompanyPage() {
  const qc = useQueryClient();
  const [created, setCreated] = useState<(SupportCreateCompanyResponse & { name: string; ownerEmail: string }) | null>(null);
  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(SupportCreateCompanyRequest),
    defaultValues: { name: '', timezone: 'America/Chicago', standardDayHours: 10, cutoffTime: '19:00', ownerName: '', ownerEmail: '' },
  });
  const create = useMutation({
    mutationFn: supportApi.createCompany,
    onSuccess: (r, input) => {
      setCreated({ ...r, name: input.name, ownerEmail: input.ownerEmail.toLowerCase() });
      void qc.invalidateQueries({ queryKey: qk.companies() });
    },
    onError: (e) => form.setError('root', { message: errorMessage(e) }),
  });
  const errors = form.formState.errors;

  if (created) return <Created c={created} />;

  return (
    <>
      <PageHeader title="New company" sub="Creates the company and its owner. Roster, equipment and cost codes follow with the tenant CLI or in the web app." />
      <form onSubmit={form.handleSubmit((v) => create.mutate(v))} noValidate className="max-w-[720px]">
        <Card>
          <CardHeader title="Company" />
          <CardBody className="form-grid">
            <Field label="Company name" htmlFor="n-name" error={errors.name?.message} wide>
              <Input id="n-name" autoFocus invalid={!!errors.name} {...form.register('name')} />
            </Field>
            <Field label="Timezone (IANA)" htmlFor="n-tz" error={errors.timezone?.message} hint="e.g. America/Chicago, America/Denver">
              <Input id="n-tz" invalid={!!errors.timezone} {...form.register('timezone')} />
            </Field>
            <Field label="Standard day (hours)" htmlFor="n-hours" error={errors.standardDayHours?.message}>
              <Input id="n-hours" type="number" min={1} max={24} step={0.5} className="w-28" invalid={!!errors.standardDayHours} {...form.register('standardDayHours', { valueAsNumber: true })} />
            </Field>
            <Field label="End-of-day cutoff" htmlFor="n-cutoff" error={errors.cutoffTime?.message}>
              <Input id="n-cutoff" type="time" className="w-32" invalid={!!errors.cutoffTime} {...form.register('cutoffTime')} />
            </Field>
          </CardBody>
          <CardHeader title="Owner" />
          <CardBody className="form-grid">
            <Field label="Owner name" htmlFor="n-owner" error={errors.ownerName?.message}>
              <Input id="n-owner" invalid={!!errors.ownerName} {...form.register('ownerName')} />
            </Field>
            <Field label="Owner email" htmlFor="n-email" error={errors.ownerEmail?.message} hint="Their sign-in; must not be used by any other company.">
              <Input id="n-email" type="email" invalid={!!errors.ownerEmail} {...form.register('ownerEmail')} />
            </Field>
            {errors.root ? (
              <div role="alert" className="w rounded-lg border border-bad bg-hi-bad px-3 py-2 text-xs font-semibold text-bad">
                {errors.root.message}
              </div>
            ) : null}
            <div className="w flex justify-end gap-2">
              <Link to="/">
                <Button>Cancel</Button>
              </Link>
              <Button type="submit" variant="primary" loading={create.isPending} data-testid="create-company">
                Create company
              </Button>
            </div>
          </CardBody>
        </Card>
      </form>
    </>
  );
}

function Created({ c }: { c: SupportCreateCompanyResponse & { name: string; ownerEmail: string } }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(c.temporaryPassword);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <>
      <PageHeader title={`${c.name} is ready`} sub={`Owner ${c.ownerEmail} · company ${c.companyId}`} />
      <Card className="max-w-[720px]">
        <CardBody className="flex flex-col gap-3">
          <div role="alert" className="flex items-start gap-2 rounded-lg border border-warn bg-warn-bg px-3 py-2 text-xs font-semibold text-ink">
            <AlertTriangle className="mt-0.5 size-4 flex-none text-warn" />
            <span>
              This temporary password is shown <b>once</b> and is not stored anywhere. Read it to the owner over the phone or paste it in a message
              that expires — never email it next to the sign-in link. It disappears when you leave this page.
            </span>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-line bg-surface2 px-3 py-2">
            <code className="flex-1 text-[16px] tracking-wider select-all" data-testid="temporary-password">
              {c.temporaryPassword}
            </code>
            <Button size="sm" onClick={() => void copy()}>
              {copied ? <Check /> : <Copy />} {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="text-xs text-ink3">
            Next: <code>tenant:import-roster {c.companyId} roster.csv</code> · <code>tenant:import-equipment {c.companyId} equipment.csv</code> ·
            jobs in the web app · <code>tenant:verify {c.companyId}</code> (RUNBOOK-tenant-provisioning.md).
          </p>
          <div className="flex justify-end gap-2">
            <Link to="/companies/new">
              <Button>Create another</Button>
            </Link>
            <Link to={`/companies/${c.companyId}`}>
              <Button variant="primary">Open company</Button>
            </Link>
          </div>
        </CardBody>
      </Card>
    </>
  );
}
